import { User } from 'lucide-react';
import type { LaunchCrewMember } from '@vtak/mod-space/client';

/** Horizontal roster of crew members (photo, name, role) for crewed missions. */
export function LaunchCrew({
  crew,
  destination,
  spacecraft,
}: {
  crew: LaunchCrewMember[];
  destination: string;
  spacecraft: string;
}): React.JSX.Element | null {
  if (crew.length === 0) return null;
  return (
    <div className="border-t border-echo-border px-4 py-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Crew</span>
        <span className="truncate text-[11px] text-foreground/80">
          {[spacecraft, destination].filter(Boolean).join('  →  ')}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {crew.map((member) => (
          <CrewCard key={`${member.name}:${member.role}`} member={member} />
        ))}
      </div>
    </div>
  );
}

/** A single crew member card. */
function CrewCard({ member }: { member: LaunchCrewMember }): React.JSX.Element {
  return (
    <div className="w-24 shrink-0 text-center">
      <CrewPhoto image={member.image} name={member.name} />
      <div className="mt-1 truncate text-xs font-medium text-foreground" title={member.name}>
        {member.name}
      </div>
      <div className="truncate text-[10px] text-muted-foreground" title={member.role}>
        {member.role}
      </div>
      {member.agency && (
        <div className="truncate text-[10px] text-echo-yellow/80" title={member.agency}>
          {member.agency}
        </div>
      )}
    </div>
  );
}

/** Crew photo with a graceful icon fallback. */
function CrewPhoto({ image, name }: { image: string; name: string }): React.JSX.Element {
  if (!image) {
    return (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-echo-border bg-white/[0.04]">
        <User className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={image}
      alt={name}
      className="mx-auto h-20 w-20 rounded-full border border-echo-border object-cover"
    />
  );
}
